/**
 * useMessageAnchorScroll — ADR-095 Phase 3.4.
 *
 * Watches the location hash (`#m_<message_id>`) and scrolls the matching
 * `<article id="m_...">` into view. Used for deep-links like
 * /c/abc#m_msg_1733049600_42 that should land the reader at a specific
 * message in the thread instead of the top.
 *
 * The 120ms timer covers the brief window between route mount + Message
 * DOM-render — without it, document.getElementById returns null because
 * the thread hasn't rendered yet on first hash arrival. 120ms is empirical
 * (Phase 1 AgentTimeline + MessageRenderer typically mount within
 * 80-100ms of route activation).
 *
 * Pure `parseMessageHash` extracted for unit testability; hook is thin
 * glue over useEffect + getElementById.
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export function parseMessageHash(hash: string): string | null {
  if (!hash) return null;
  const clean = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!clean.startsWith("m_") || clean.length <= 2) return null;
  return clean;
}

export function useMessageAnchorScroll(): void {
  const location = useLocation();

  useEffect(() => {
    const elementId = parseMessageHash(location.hash);
    if (!elementId) return;
    const timer = window.setTimeout(() => {
      const el = document.getElementById(elementId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 120);
    return () => {
      window.clearTimeout(timer);
    };
  }, [location.hash]);
}
